'use client';

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Sparkles,
  Loader2,
  Info,
  Wand2,
  Check,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Video as VideoIcon,
  Paperclip,
  SlidersHorizontal,
  Volume2,
  VolumeX,
  Clock,
  Gauge,
  Ratio,
  Search,
  X,
} from 'lucide-react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useAuth } from '@/contexts/AuthContext';
import { DynamicParamField, ParamDef } from './DynamicParamField';
import { AspectRatioPicker } from './AspectRatioPicker';
import { ReferenceImages } from './ReferenceImages';
import { ModelInfoPopup } from './ModelInfoPopup';
import { WavespeedPrice } from './WavespeedPrice';
import { BrandIcon } from './BrandIcon';
import { useI18n } from '@/contexts/I18nContext';
import { useWavespeedCatalog } from '@/hooks/useWavespeedCatalog';
import { useWavespeedQuote } from '@/hooks/useWavespeedQuote';
import { isMediaParam, referenceParams, resolveGeneration } from '@/lib/studio/generation-inputs';
import type { WavespeedModel } from '@/lib/ai/wavespeed';

type GenerationType = 'image' | 'video';

type ModelDTO = WavespeedModel;

/**
 * Renders children into document.body via React portal, positioned just
 * below an anchor element. This bypasses any stacking context an ancestor
 * might create with `transform`, `filter`, `backdrop-filter`, etc., so the
 * popover always sits above the rest of the page (including the examples
 * grid which has hover transforms).
 */
type Placement = 'bottom-start' | 'bottom-center' | 'bottom-end' | 'right-start' | 'left-start';

function PortalPopover({
  anchorRef,
  placement = 'bottom-start',
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  placement?: Placement;
  children: React.ReactNode;
}) {
  const [coords, setCoords] = useState<{
    top: number;
    left?: number;
    right?: number;
    maxHeight: number;
    maxWidth?: number;
    ready: boolean;
  }>({ top: 0, left: 0, maxHeight: 0, ready: false });
  const popRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const update = () => {
      const a = anchorRef.current;
      const p = popRef.current;
      if (!a) return;
      const ar = a.getBoundingClientRect();
      const pw = p?.offsetWidth ?? 0;
      const ph = p?.offsetHeight ?? 0;
      const margin = 8;
      const isBottom =
        placement === 'bottom-start' ||
        placement === 'bottom-end' ||
        placement === 'bottom-center';

      let top = 0;
      let left: number | undefined;
      let right: number | undefined;
      let maxHeight = window.innerHeight - margin * 2;
      let maxWidth: number | undefined;

      if (isBottom) {
        // Vertical: pick whichever side (below / above) has more room,
        // preferring below when it has at least ~240px. Never overlap the
        // anchor.
        const roomBelow = window.innerHeight - ar.bottom - margin * 2;
        const roomAbove = ar.top - margin * 2;
        const preferBelow = roomBelow >= 240 || roomBelow >= roomAbove;
        if (preferBelow) {
          top = ar.bottom + margin;
          maxHeight = Math.max(120, roomBelow);
        } else {
          maxHeight = Math.max(120, roomAbove);
          const useH = ph > 0 ? Math.min(ph, maxHeight) : maxHeight;
          top = Math.max(margin, ar.top - useH - margin);
        }

        // Horizontal: pin the popover's LEADING (left in LTR) edge to the
        // anchor's left edge for bottom-start. Pin the trailing edge for
        // bottom-end. Center for bottom-center.
        if (placement === 'bottom-end') {
          right = window.innerWidth - ar.right;
          if (right < margin) right = margin;
          maxWidth = window.innerWidth - right - margin;
        } else if (placement === 'bottom-center') {
          left = ar.left + ar.width / 2 - (pw || 0) / 2;
          if (left < margin) left = margin;
          const maxLeft = Math.max(margin, window.innerWidth - (pw || 320) - margin);
          if (left > maxLeft) left = maxLeft;
          maxWidth = window.innerWidth - margin * 2;
        } else {
          // bottom-start
          left = ar.left;
          if (left < margin) left = margin;
          maxWidth = window.innerWidth - left - margin;
        }
      } else if (placement === 'right-start') {
        top = ar.top;
        left = ar.right + margin;
        if (pw && left + pw > window.innerWidth - margin) {
          left = ar.left - (pw || 0) - margin;
        }
        if (left < margin) left = margin;
        maxHeight = Math.max(120, window.innerHeight - top - margin);
      } else if (placement === 'left-start') {
        top = ar.top;
        left = ar.left - (pw || 0) - margin;
        if (left < margin) left = margin;
        maxHeight = Math.max(120, window.innerHeight - top - margin);
      }

      setCoords({ top, left, right, maxHeight, maxWidth, ready: true });
    };
    update();
    const raf = window.requestAnimationFrame(update);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef, placement]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={popRef}
      // The portal lives outside the studio's `data-lenis-prevent` region,
      // so Lenis would otherwise hijack wheel/trackpad events inside the
      // popover. Tag the root to opt this subtree out of Lenis.
      data-lenis-prevent
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        right: coords.right,
        maxHeight: coords.maxHeight || undefined,
        maxWidth: coords.maxWidth || undefined,
        zIndex: 100,
        visibility: coords.ready ? 'visible' : 'hidden',
        display: 'flex',
      }}
      // Swallow pointer events so the document-level click-outside listener
      // (which fires on mousedown/touchstart) doesn't close us on inside taps.
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}

/**
 * Keys of model parameters that get surfaced as inline pills in the prompt
 * toolbar instead of (or in addition to) the Settings popover. Order here
 * controls the visual order of the pills.
 */
/** Toolbar chip — the one pill shape shared by every control under the prompt. */
const CHIP =
  'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-[var(--st-line)] px-3 text-[13px] font-semibold text-[var(--st-text)] transition';
/** Square variant for icon-only chips (references, settings, sound). */
const CHIP_ICON =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--st-line)] transition';

const PILL_KEYS = [
  'aspect_ratio',
  'duration',
  'resolution',
  'quality',
] as const;

// Any param whose key contains "audio" (or equals "sound") is treated as the
// audio toggle — schemas use many spellings (`audio`, `audio_enabled`,
// `enable_audio`, `has_audio`, …).
function isAudioKey(key: string): boolean {
  return /audio/i.test(key) || key === 'sound';
}

/**
 * Inline pill in the prompt toolbar that surfaces a single popular param
 * (e.g. aspect ratio, duration, sound, quality). Clicking opens a small
 * portal popover with the param's choices.
 */
function ParamPill({
  paramKey,
  param,
  value,
  onChange,
}: {
  paramKey: string;
  param: ParamDef;
  value: any;
  onChange: (v: any) => void;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (anchorRef.current && !anchorRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  const v = value ?? param.default;
  const isBool = param.type === 'boolean' || param.widget === 'toggle';

  // sound / audio (any key containing "audio") render as a one-click
  // toggle pill: no popover, no chevron — just the icon swap and an
  // instant flip of the value.
  if (isAudioKey(paramKey) && isBool) {
    const on = !!v;
    return (
      <button
        type="button"
        onClick={() => onChange(!on)}
        title={paramKey}
        aria-pressed={on}
        className={`${CHIP_ICON} hover:bg-[var(--st-panel)] ${
          on ? 'bg-[var(--st-violet)] text-white' : 'bg-[var(--st-chip)] text-[var(--st-muted)]'
        }`}
      >
        {on ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
      </button>
    );
  }

  // Compact in-pill display.
  let LeadingIcon: React.ComponentType<{ className?: string }> | null = null;
  let displayText: string;
  if (paramKey === 'aspect_ratio') {
    LeadingIcon = Ratio;
    displayText = v != null ? String(v) : 'Auto';
  } else if (paramKey === 'duration') {
    LeadingIcon = Clock;
    displayText = v != null ? `${v}s` : '—';
  } else if (isAudioKey(paramKey)) {
    LeadingIcon = v ? Volume2 : VolumeX;
    displayText = isBool ? (v ? 'On' : 'Off') : v != null ? String(v) : '—';
  } else if (paramKey === 'quality' || paramKey === 'resolution') {
    LeadingIcon = Gauge;
    displayText = v != null ? String(v).toUpperCase() : '—';
  } else if (isBool) {
    displayText = v ? 'On' : 'Off';
  } else {
    displayText = v != null ? String(v) : '—';
  }

  return (
    <div ref={anchorRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        className={`${CHIP} ${open ? 'bg-[var(--st-panel)]' : 'bg-[var(--st-chip)] hover:bg-[var(--st-panel)]'}`}
        title={paramKey.replace(/_/g, ' ')}
      >
        {LeadingIcon && <LeadingIcon className="h-[15px] w-[15px] text-[var(--st-muted)]" />}
        <span>{displayText}</span>
        <ChevronDown className="h-3.5 w-3.5 text-[var(--st-muted)]" />
      </button>
      {open && (
        <PortalPopover anchorRef={anchorRef} placement="bottom-start">
          <div
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[inherit] w-[15rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[var(--st-line)] bg-[var(--st-panel)]/95 text-white shadow-2xl backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-150 ease-out"
          >
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {paramKey === 'aspect_ratio' ? (
                <AspectGrid
                  value={v ?? 'auto'}
                  onChange={(nv) => {
                    onChange(nv);
                    setOpen(false);
                  }}
                  allowed={param.enum?.map(String)}
                />
              ) : isBool ? (
                <div className="grid grid-cols-2 gap-1.5">
                  {[true, false].map((opt) => {
                    const active = v === opt;
                    return (
                      <button
                        key={String(opt)}
                        type="button"
                        onClick={() => {
                          onChange(opt);
                          setOpen(false);
                        }}
                        className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                          active
                            ? 'border-[var(--st-violet)] bg-[var(--st-violet)]/20 text-white'
                            : 'border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/10'
                        }`}
                      >
                        {opt ? 'On' : 'Off'}
                      </button>
                    );
                  })}
                </div>
              ) : param.enum && param.enum.length > 0 ? (
                <ul className="space-y-1">
                  {param.enum.map((opt) => {
                    const active = String(opt) === String(v);
                    return (
                      <li key={String(opt)}>
                        <button
                          type="button"
                          onClick={() => {
                            onChange(opt);
                            setOpen(false);
                          }}
                          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm font-semibold transition ${
                            active
                              ? 'border-[var(--st-violet)] bg-[var(--st-violet)]/20 text-white'
                              : 'border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/10'
                          }`}
                        >
                          <span>{String(opt)}</span>
                          {active && <Check className="h-3.5 w-3.5" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <DynamicParamField
                  param={param}
                  value={value}
                  onChange={(nv) => onChange(nv)}
                />
              )}
            </div>
          </div>
        </PortalPopover>
      )}
    </div>
  );
}

/** What was actually asked for — enough for the stage below the composer to
 *  label the in-flight job without re-deriving it from the model schema. */
export interface SubmittedInfo {
  type: GenerationType;
  prompt: string;
  modelLabel: string;
  aspect?: string;
  duration?: string;
  resolution?: string;
}

interface Props {
  type: GenerationType;
  onTypeChange: (type: GenerationType) => void;
  onSubmitted?: (jobId: string, info?: SubmittedInfo) => void;
  onModelChange?: (id: string) => void;
  promptValue?: string;
  onPromptChange?: (v: string) => void;
  /** Hide the motivational heading + subtitle. Used when embedding the
   *  generator in a tighter context (e.g. the library's bottom prompt). */
  compact?: boolean;
  /** Bump to re-submit the current prompt without the user touching the
   *  composer — drives the stage's "generate again" action. */
  submitToken?: number;
  /** A generation is still running downstream. The POST has long since
   *  resolved, so `submitting` alone would flip the button back to idle
   *  while the job is still cooking. */
  busy?: boolean;
}

export function HeroGenerator({
  type,
  onTypeChange,
  onSubmitted,
  onModelChange,
  promptValue,
  onPromptChange,
  compact = false,
  submitToken = 0,
  busy = false,
}: Props) {
  const { authedFetch } = useAuthToken();
  const { checkAuth } = useAuth();
  const { t } = useI18n();

  const { models: allModels, loading: modelsLoading, stale: catalogStale } = useWavespeedCatalog();
  const [selectedByType, setSelectedByType] = useState({ image: '', video: '' });
  const [prompt, setPromptState] = useState('');
  const [paramValues, setParamValues] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [referencesOpen, setReferencesOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Which settings row is currently expanded inside the settings popover. */
  const [settingsExpanded, setSettingsExpanded] = useState<string | null>(null);
  /** Which references row is currently expanded. */
  const [referencesExpanded, setReferencesExpanded] = useState<string | null>(null);
  const settingsAnchorRef = useRef<HTMLDivElement | null>(null);
  const referencesAnchorRef = useRef<HTMLDivElement | null>(null);
  const modelPickerAnchorRef = useRef<HTMLDivElement | null>(null);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);

  // Close popovers on outside click / Escape. Single handler covers all three —
  // a click outside any open anchor closes only its corresponding popover.
  useEffect(() => {
    const anyOpen = settingsOpen || referencesOpen || modelPickerOpen;
    if (!anyOpen) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (settingsOpen && settingsAnchorRef.current && !settingsAnchorRef.current.contains(target)) {
        setSettingsOpen(false);
      }
      if (referencesOpen && referencesAnchorRef.current && !referencesAnchorRef.current.contains(target)) {
        setReferencesOpen(false);
      }
      if (modelPickerOpen && modelPickerAnchorRef.current && !modelPickerAnchorRef.current.contains(target)) {
        setModelPickerOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setSettingsOpen(false);
      setReferencesOpen(false);
      setModelPickerOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [settingsOpen, referencesOpen, modelPickerOpen]);

  const taRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // Compact mode (library bottom prompt) caps lower so the textarea can't
    // grow tall enough to crowd the gallery above it.
    const maxH = compact ? 140 : 360;
    el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
  }, [prompt, compact]);

  useEffect(() => {
    if (promptValue !== undefined && promptValue !== prompt) {
      setPromptState(promptValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptValue]);

  const setPrompt = (v: string) => {
    setPromptState(v);
    onPromptChange?.(v);
  };

  // Current view: models of the active type + which one is selected for it.
  const models = useMemo(
    () => allModels.filter((m) => m.type === type),
    [allModels, type]
  );
  const selectedModel = selectedByType[type] ?? '';

  const setModelExternal = (id: string) => {
    setSelectedByType((prev) => ({ ...prev, [type]: id }));
    setParamValues({});
    onModelChange?.(id);
  };

  useEffect(() => {
    setSelectedByType((previous) => {
      const image = allModels.some((m) => m.type === 'image' && m.id === previous.image)
        ? previous.image : allModels.find((m) => m.type === 'image')?.id ?? '';
      const video = allModels.some((m) => m.type === 'video' && m.id === previous.video)
        ? previous.video : allModels.find((m) => m.type === 'video')?.id ?? '';
      return image === previous.image && video === previous.video ? previous : { image, video };
    });
  }, [allModels]);

  // When the user toggles type, reset params (model-specific) and propagate
  // the (already-loaded) model id outward so the parent's `activeModel`
  // (used by examples) follows the segment.
  useEffect(() => {
    setParamValues({});
    if (selectedModel) onModelChange?.(selectedModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, selectedModel]);

  const model = allModels.find((m) => m.id === selectedModel);
  const resolved = resolveGeneration(model, paramValues);
  const schema = resolved.schema;
  const missingRequired = schema.some((p) => p.required &&
    (resolved.params[p.key] === undefined || resolved.params[p.key] === '' ||
      (Array.isArray(resolved.params[p.key]) && !resolved.params[p.key].length)));
  const quote = useWavespeedQuote(resolved.modelId, { ...resolved.params, prompt }, !!model && !missingRequired);
  const batch = Math.max(1, Math.min(4, Number(paramValues.__batch) || 1));
  const priceId = useId();

  const siblings = useMemo(() => {
    if (!model) return [];
    return models.filter((m) => m.id !== model.id && m.label === model.label);
  }, [models, model]);

  const mediaParams = referenceParams(model, schema);
  const singleImageParams = mediaParams.filter((s) => s.widget === 'image-url');
  // Promoted to inline pills in the prompt toolbar — keep them out of the
  // Settings popover so we don't duplicate the controls.
  const pillParams: ParamDef[] = [];
  for (const k of PILL_KEYS) {
    const p = schema.find((s) => s.key === k);
    if (p) pillParams.push(p);
  }
  // Audio-like params: matched by substring so `audio`, `audio_enabled`,
  // `enable_audio`, etc. all surface as a toggle pill. Only render boolean
  // variants — text/number/enum stays in the Settings popover.
  const audioPillParams = schema.filter(
    (s) =>
      isAudioKey(s.key) &&
      (s.type === 'boolean' || s.widget === 'toggle') &&
      !pillParams.some((p) => p.key === s.key)
  );
  pillParams.push(...audioPillParams);

  const pillKeySet = new Set(pillParams.map((p) => p.key));
  const otherParams = schema.filter(
    (s) => !isMediaParam(s) && !pillKeySet.has(s.key)
  );

  const referencesCount = mediaParams.reduce((sum, param) => {
    const value = paramValues[param.key];
    return sum + (Array.isArray(value) ? value.length : value ? 1 : 0);
  }, 0);

  async function enhancePrompt() {
    if (!prompt.trim() || enhancing) return;
    setEnhancing(true);
    setError(null);
    try {
      const res = await authedFetch('/api/ai/prompt-enhance', {
        method: 'POST',
        body: JSON.stringify({ prompt, type }),
      });
      const json = await res.json();
      if (!json.success) {
        if (res.status === 402) throw new Error(t('studio.generator.notEnoughEnhance'));
        throw new Error(json.error || t('studio.generator.enhanceFailed'));
      }
      setPrompt(json.data.enhanced);
    } catch (err: any) {
      setError(err?.message || t('studio.generator.failed'));
    } finally {
      setEnhancing(false);
    }
  }

  async function handleSubmit() {
    if (!selectedModel || !prompt.trim() || quote.price === undefined || missingRequired) return;
    setSubmitting(true);
    setError(null);
    try {
      const params = resolved.params;
      const submittedInfo: SubmittedInfo = {
        type,
        prompt,
        modelLabel: model?.label ?? '',
        aspect: params.aspect_ratio != null ? String(params.aspect_ratio) : undefined,
        duration: params.duration != null ? `${params.duration}s` : undefined,
        resolution:
          params.resolution != null
            ? String(params.resolution).toUpperCase()
            : params.quality != null
              ? String(params.quality).toUpperCase()
              : undefined,
      };
      const submits = await Promise.all(
        Array.from({ length: batch }).map(() =>
          authedFetch('/api/ai/wavespeed/submit', {
            method: 'POST',
            body: JSON.stringify({ model: resolved.modelId, prompt, params: resolved.params, expectedPrice: quote.price }),
          }).then((r) => r.json().then((j) => ({ status: r.status, json: j })))
        )
      );
      for (const { status, json } of submits) {
        if (!json.success) {
          if (status === 402 && json.data) {
            throw new Error(
              t('studio.generator.notEnoughTokens', {
                required: Number(json.data.required).toFixed(2),
                available: Number(json.data.available).toFixed(2),
              })
            );
          }
          if (json.code === 'PRICE_CHANGED') {
            quote.updatePrice(json.data);
            throw new Error(t('studio.generator.priceChanged'));
          }
          throw new Error(json.error || t('studio.generator.submitFailed'));
        }
        onSubmitted?.(json.data.jobId, submittedInfo);
      }
      checkAuth();
    } catch (err: any) {
      setError(err?.message || t('studio.generator.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  // Re-submit on demand (stage "again" button). Skipped on mount so a fresh
  // page load never fires a generation by itself.
  const submitTokenRef = useRef(submitToken);
  useEffect(() => {
    if (submitToken === submitTokenRef.current) return;
    submitTokenRef.current = submitToken;
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitToken]);

  const promptPlaceholder =
    type === 'video'
      ? t('studio.generator.promptPlaceholderVideo')
      : t('studio.generator.promptPlaceholderImage');

  return (
    <div className="w-full">
      {/* Motivational hero copy */}
      {!compact && (
      <div className="mb-5 text-center">
        <h1 className="text-[28px] font-extrabold leading-tight tracking-tight text-white sm:text-[34px]">
          {t('studio.generator.heroTitle')}
        </h1>
        <p className="mt-6 text-[15px] font-medium text-[var(--st-muted)] sm:mt-[29px]">
          {t('studio.generator.heroSubtitle')}
        </p>
      </div>
      )}

      <div className="mb-3">
        {/* Two-row card: prompt on top, every control on one toolbar under
            it. The enhance wand is the prompt's leading glyph rather than a
            button of its own — it belongs to the text, not the toolbar. */}
        <div className="flex flex-col gap-3.5 rounded-[22px] border border-[var(--st-line)] bg-[var(--st-panel)] p-4 pb-3.5">
          {/* TOP row — Enhance (left) + textarea */}
          <div className="flex min-h-[46px] items-start gap-2.5">
            <button
              type="button"
              onClick={enhancePrompt}
              disabled={!prompt.trim() || enhancing}
              title={t('studio.generator.enhanceTitle')}
              className="mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center text-[var(--st-violet)] transition hover:brightness-125 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {enhancing ? (
                <Loader2 className="h-[18px] w-[18px] animate-spin" />
              ) : (
                <Wand2 className="h-[18px] w-[18px]" />
              )}
            </button>
            <textarea
              ref={taRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={promptPlaceholder}
              rows={1}
              className="block min-w-0 flex-1 resize-none bg-transparent text-[14.5px] leading-6 text-[var(--st-text)] outline-none placeholder:text-[var(--st-muted)] focus:ring-0"
            />
          </div>

          {/* Give mobile controls the full width; keep the complete price
              visible below them. On larger screens Generate trails the row. */}
          <div className="flex flex-col items-end gap-2 sm:flex-row">
            <div className="flex w-full min-w-0 flex-1 flex-wrap items-center gap-2 sm:w-auto">
              {/* Image / video segment — first toolbar item, horizontal */}
              <div className="inline-flex h-9 shrink-0 items-center rounded-full border border-[var(--st-line)] bg-[var(--st-chip)] p-1">
                {([
                  ['image', ImageIcon, 'studio.generator.modeImage'],
                  ['video', VideoIcon, 'studio.generator.modeVideo'],
                ] as const).map(([mode, Icon, labelKey]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => onTypeChange(mode)}
                    aria-label={t(labelKey)}
                    aria-pressed={type === mode}
                    title={t(labelKey)}
                    className={`flex h-7 w-9 items-center justify-center rounded-full transition ${
                      type === mode
                        ? 'bg-[var(--st-violet)] text-white'
                        : 'text-[var(--st-muted)] hover:text-[var(--st-text)]'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>

              {/* Model pill — popover drops out below */}
              <div ref={modelPickerAnchorRef} className="relative">
                <button
                  type="button"
                  onClick={() => setModelPickerOpen((v) => !v)}
                  disabled={modelsLoading || models.length === 0}
                  className={`${CHIP} disabled:opacity-50 ${
                    modelPickerOpen ? 'bg-[var(--st-panel)]' : 'bg-[var(--st-chip)] hover:bg-[var(--st-panel)]'
                  }`}
                  title={t('studio.generator.model')}
                >
                  {model ? (
                    <BrandIcon
                      modelId={model.id}
                      fallbackLetter={model.brand?.letter}
                      size={18}
                      rounded="md"
                    />
                  ) : (
                    <Sparkles className="h-[15px] w-[15px] text-[var(--st-muted)]" />
                  )}
                  <span className="max-w-[160px] truncate">
                    {model?.label ?? t('studio.generator.chooseModel')}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-[var(--st-muted)]" />
                </button>
                {modelPickerOpen && (
                  <ModelPickerPopover
                    anchorRef={modelPickerAnchorRef}
                    models={models}
                    type={type}
                    loading={modelsLoading}
                    selectedModel={selectedModel}
                    onSelect={(id) => {
                      setModelExternal(id);
                      setModelPickerOpen(false);
                    }}
                    onShowInfo={() => {
                      setModelPickerOpen(false);
                      setInfoOpen(true);
                    }}
                    hasModel={!!model}
                  />
                )}
              </div>

              {/* Inline quick-access pills for the most-tweaked params
                  (aspect, duration, sound, quality). Only render the ones
                  the active model actually exposes. */}
              {pillParams.map((p) => (
                <ParamPill
                  key={p.key}
                  paramKey={p.key}
                  param={p}
                  value={paramValues[p.key]}
                  onChange={(nv) =>
                    setParamValues((prev) => ({ ...prev, [p.key]: nv }))
                  }
                />
              ))}

              {/* References — popover drops out below */}
              <div ref={referencesAnchorRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setReferencesOpen((v) => !v);
                    setReferencesExpanded(null);
                  }}
                  className={`${referencesCount > 0 ? CHIP : CHIP_ICON} ${
                    referencesOpen ? 'bg-[var(--st-panel)]' : 'bg-[var(--st-chip)] hover:bg-[var(--st-panel)]'
                  }`}
                  title={t('studio.generator.addReferences')}
                >
                  <Paperclip className="h-4 w-4 text-[var(--st-muted)]" />
                  {referencesCount > 0 ? referencesCount : ''}
                </button>
                {referencesOpen && (
                  <ReferencesPopover
                    anchorRef={referencesAnchorRef}
                    mediaParams={mediaParams}
                    paramValues={paramValues}
                    setParamValues={setParamValues}
                    singleImageParams={singleImageParams}
                    expandedKey={referencesExpanded}
                    setExpandedKey={setReferencesExpanded}
                  />
                )}
              </div>

              {/* Settings — popover drops out from under the button */}
              <div ref={settingsAnchorRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setSettingsOpen((v) => !v);
                    setSettingsExpanded(null);
                  }}
                  className={`${CHIP_ICON} ${
                    settingsOpen ? 'bg-[var(--st-panel)]' : 'bg-[var(--st-chip)] hover:bg-[var(--st-panel)]'
                  }`}
                  title={t('studio.generator.settings')}
                >
                  <SlidersHorizontal className="h-4 w-4 text-[var(--st-muted)]" />
                </button>
                {settingsOpen && (
                  <SettingsPopover
                    anchorRef={settingsAnchorRef}
                    otherParams={otherParams}
                    paramValues={paramValues}
                    setParamValues={setParamValues}
                    expandedKey={settingsExpanded}
                    setExpandedKey={setSettingsExpanded}
                  />
                )}
              </div>
            </div>

            {/* Keep pricing in its own chip so quote changes never resize Generate. */}
            <div className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2">
              {!submitting && !busy && quote.price !== undefined && (
                <span className="inline-flex h-9 items-center rounded-full border border-[var(--st-line)] bg-[var(--st-chip)] px-3 text-[12px] font-semibold text-[var(--st-text)]">
                  <WavespeedPrice id={priceId} quote={quote} quantity={batch} layout="inline" />
                </span>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!selectedModel || !prompt.trim() || submitting || busy || quote.price === undefined || missingRequired}
                title={t('studio.generator.generate')}
                aria-label={t('studio.generator.generate')}
                aria-describedby={!submitting && !busy && quote.price !== undefined ? priceId : undefined}
                className="flex h-[41px] shrink-0 items-center gap-2 rounded-[14px] bg-[var(--st-violet)] px-4 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-[#3A2F5E] disabled:opacity-100"
              >
                <span className="hidden sm:inline">
                  {submitting || busy ? t('studio.generating') : t('studio.generator.generate')}
                </span>
                {submitting || busy ? (
                  <Loader2 className="h-[15px] w-[15px] animate-spin" />
                ) : (
                  <Sparkles className="h-[15px] w-[15px]" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {(catalogStale || quote.error || missingRequired) && (
        <p role="status" className="mb-3 text-xs text-amber-200">
          {missingRequired ? t('studio.generator.requiredInputs') : quote.error
            ? t('studio.generator.priceUnavailable') : t('studio.generator.catalogStale')}
        </p>
      )}
      {error && (
        <div className="mb-4 rounded-2xl border border-[#F0324B]/40 bg-[#F0324B]/10 p-3 text-xs text-[#FFB4BE]">
          {error}
        </div>
      )}

      <ModelInfoPopup
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        model={
          model && {
            id: model.id,
            label: model.label,
            type: model.type,
            brand: model.brand,
            description: model.description,
            estimatedCost: model.estimatedCost,
            formula: model.formula,
            badge: model.badge,
          }
        }
        siblings={siblings.map((s) => ({
          id: s.id,
          label: `${s.label} (${s.subtype})`,
          type: s.type,
          brand: s.brand,
          description: s.description,
          estimatedCost: s.estimatedCost,
          formula: s.formula,
          badge: s.badge,
        }))}
        onSelect={(id) => setModelExternal(id)}
      />
    </div>
  );
}

/**
 * Compact settings popover that drops out from under the ⚙ button in the
 * prompt toolbar. Rows are pills — each shows a label + the current value;
 * clicking opens a SECONDARY popover next to that row with just its picker.
 */
function SettingsPopover({
  anchorRef,
  otherParams,
  paramValues,
  setParamValues,
  expandedKey,
  setExpandedKey,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  otherParams: ParamDef[];
  paramValues: Record<string, any>;
  setParamValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  expandedKey: string | null;
  setExpandedKey: (k: string | null) => void;
}) {
  const { t } = useI18n();

  function prettyValue(p: ParamDef): string {
    const v = paramValues[p.key] ?? p.default;
    if (v === undefined || v === null || v === '') return '—';
    // Boolean / toggle widgets read as "On" / "Off" instead of "true" / "false".
    if (typeof v === 'boolean' || p.widget === 'toggle') {
      return v ? 'On' : 'Off';
    }
    return String(v);
  }
  function prettyKeyLabel(p: ParamDef): string {
    const labelKey = `studio.param.${p.key}`;
    const translated = t(labelKey);
    if (translated !== labelKey) return translated;
    return p.key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  // Per-row DOM refs so the sub-picker can anchor to the active row.
  const rowRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const subAnchorRef = useMemo<React.RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        return expandedKey ? rowRefs.current[expandedKey] : null;
      },
      set current(_) {
        /* no-op — ref is derived */
      },
    }),
    [expandedKey]
  );

  return (
    <>
      <PortalPopover anchorRef={anchorRef} placement="bottom-start">
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex max-h-[inherit] w-[19rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[var(--st-line)] bg-[var(--st-panel)]/95 text-white shadow-2xl backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-150 ease-out"
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            <ul className="space-y-1">
              {otherParams.map((p) => (
                <SettingsRow
                  key={p.key}
                  k={p.key}
                  label={prettyKeyLabel(p)}
                  value={prettyValue(p)}
                  active={expandedKey === p.key}
                  onActivate={() => setExpandedKey(p.key)}
                  registerRef={(el) => {
                    rowRefs.current[p.key] = el;
                  }}
                />
              ))}

              <SettingsRow
                k="__batch"
                label={t('studio.generator.batchCount')}
                value={String(paramValues.__batch ?? 1)}
                active={expandedKey === '__batch'}
                onActivate={() => setExpandedKey('__batch')}
                registerRef={(el) => {
                  rowRefs.current.__batch = el;
                }}
              />

              <SettingsRow
                k="seed"
                label={t('studio.generator.seed')}
                value={paramValues.seed != null ? String(paramValues.seed) : '—'}
                active={expandedKey === 'seed'}
                onActivate={() => setExpandedKey('seed')}
                registerRef={(el) => {
                  rowRefs.current.seed = el;
                }}
              />
            </ul>
          </div>
        </div>
      </PortalPopover>

      {expandedKey && (
        <PortalPopover
          key={expandedKey}
          anchorRef={subAnchorRef}
          placement="right-start"
        >
          <SubPicker
            title={
              expandedKey === '__batch'
                ? t('studio.generator.batchCount')
                : expandedKey === 'seed'
                ? t('studio.generator.seed')
                : prettyKeyLabel(
                    otherParams.find((p) => p.key === expandedKey) as ParamDef
                  )
            }
            onClose={() => setExpandedKey(null)}
          >
            {expandedKey === '__batch' && (
              <input
                type="number"
                min={1}
                max={4}
                value={paramValues.__batch ?? 1}
                onChange={(e) =>
                  setParamValues((prev) => ({
                    ...prev,
                    __batch: Math.max(1, Math.min(4, parseInt(e.target.value, 10) || 1)),
                  }))
                }
                className="w-full rounded-xl border border-white/15 bg-white/10 px-2 py-1.5 text-sm text-white outline-none transition placeholder:text-white/40 focus:border-[var(--st-violet)] focus:ring-2 focus:ring-[var(--st-violet)]/25"
              />
            )}
            {expandedKey === 'seed' && (
              <input
                type="number"
                value={paramValues.seed ?? ''}
                onChange={(e) =>
                  setParamValues((prev) => ({
                    ...prev,
                    seed: e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                  }))
                }
                placeholder={t('studio.generator.seedPlaceholder')}
                className="w-full rounded-xl border border-white/15 bg-white/10 px-2 py-1.5 text-sm text-white outline-none transition placeholder:text-white/40 focus:border-[var(--st-violet)] focus:ring-2 focus:ring-[var(--st-violet)]/25"
              />
            )}
            {expandedKey !== 'aspect_ratio' &&
              expandedKey !== '__batch' &&
              expandedKey !== 'seed' && (
                <ParamSubEditor
                  param={otherParams.find((p) => p.key === expandedKey) as ParamDef}
                  value={paramValues[expandedKey]}
                  onChange={(v) =>
                    setParamValues((prev) => ({ ...prev, [expandedKey]: v }))
                  }
                  onPicked={() => setExpandedKey(null)}
                />
              )}
          </SubPicker>
        </PortalPopover>
      )}
    </>
  );
}

function SettingsRow({
  k,
  label,
  value,
  active,
  onActivate,
  registerRef,
}: {
  k: string;
  label: string;
  value: string;
  active: boolean;
  onActivate: () => void;
  registerRef: (el: HTMLLIElement | null) => void;
}) {
  return (
    <li
      ref={registerRef}
      className={`overflow-hidden rounded-xl transition ${
        active
          ? 'bg-[var(--st-violet)]/15 ring-1 ring-[var(--st-violet)]/40'
          : 'bg-white/[0.04] hover:bg-white/[0.08]'
      }`}
    >
      <button
        type="button"
        onClick={onActivate}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
        aria-expanded={active}
        aria-controls={`settings-row-${k}`}
      >
        <span className="text-sm font-medium text-white/90">{label}</span>
        <span className="flex items-center gap-1 text-sm font-semibold text-white">
          {value}
          <ChevronRight className="h-4 w-4 text-white/50" />
        </span>
      </button>
    </li>
  );
}

/**
 * Row variant used by the References popover — inline-expandable because the
 * uploader is bulky (drag-drop + previews) and wouldn't fit a thin
 * right-side sub-picker.
 */
function ExpandableRow({
  k,
  label,
  value,
  expanded,
  onToggle,
  children,
}: {
  k: string;
  label: string;
  value: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <li
      className={`overflow-hidden rounded-xl transition ${
        expanded
          ? 'bg-[var(--st-violet)]/15 ring-1 ring-[var(--st-violet)]/40'
          : 'bg-white/[0.04] hover:bg-white/[0.08]'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
        aria-expanded={expanded}
        aria-controls={`expandable-row-${k}`}
      >
        <span className="text-sm font-medium text-white/90">{label}</span>
        <span className="flex items-center gap-1 text-sm font-semibold text-white">
          {value}
          <ChevronRight
            className={`h-4 w-4 text-white/50 transition-transform ${
              expanded ? 'rotate-90' : ''
            }`}
          />
        </span>
      </button>
      {expanded && (
        <div
          id={`expandable-row-${k}`}
          className="border-t border-white/10 px-3 py-3"
        >
          {children}
        </div>
      )}
    </li>
  );
}

/**
 * Generic shell for sub-pickers — small popover with a title + close + body.
 */
function SubPicker({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex max-h-[inherit] w-[16rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[var(--st-line)] bg-[var(--st-panel)]/95 text-white shadow-2xl backdrop-blur-xl animate-in fade-in slide-in-from-left-2 duration-150 ease-out"
    >
      <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-3 py-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-white/60">
          {title}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-0.5 text-white/60 transition hover:bg-white/10 hover:text-white"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">{children}</div>
    </div>
  );
}

/**
 * Aspect-ratio choices laid out as a 2-column grid of icon-tiles, matching
 * the screenshot the user shared.
 */
function AspectGrid({
  value,
  onChange,
  allowed,
}: {
  value: string;
  onChange: (v: string) => void;
  allowed?: string[];
}) {
  const items = [
    { value: 'auto', w: 22, h: 22 },
    { value: '16:9', w: 28, h: 16 },
    { value: '9:16', w: 16, h: 28 },
    { value: '4:3', w: 24, h: 18 },
    { value: '3:4', w: 18, h: 24 },
    { value: '1:1', w: 22, h: 22 },
    { value: '21:9', w: 32, h: 14 },
    { value: '3:2', w: 27, h: 18 },
    { value: '2:3', w: 18, h: 27 },
    { value: '4:5', w: 18, h: 22 },
    { value: '5:4', w: 24, h: 19 },
  ].filter((r) => !allowed || allowed.includes(r.value));

  return (
    <div className="grid grid-cols-2 gap-1.5">
      {items.map((r) => {
        const active = value === r.value;
        return (
          <button
            key={r.value}
            type="button"
            onClick={() => onChange(r.value)}
            className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 transition ${
              active
                ? 'border-[var(--st-violet)] bg-[var(--st-violet)]/20 text-white'
                : 'border-white/10 bg-white/[0.04] text-white/80 hover:border-white/20 hover:bg-white/10'
            }`}
          >
            <span
              className={`block shrink-0 rounded-[2px] border ${
                active ? 'border-[var(--st-violet)]' : 'border-white/40'
              }`}
              style={{ width: r.w, height: r.h }}
            />
            <span className="text-sm font-semibold capitalize">{r.value}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Sub-editor for a single dynamic param. Enum-select widgets become a list
 * of choices (each clickable). Other widgets fall back to DynamicParamField.
 */
function ParamSubEditor({
  param,
  value,
  onChange,
  onPicked,
}: {
  param: ParamDef;
  value: any;
  onChange: (v: any) => void;
  onPicked: () => void;
}) {
  if (param && param.widget === 'select' && param.enum) {
    return (
      <ul className="space-y-1">
        {param.enum.map((opt) => {
          const active = String(opt) === String(value ?? param.default);
          return (
            <li key={String(opt)}>
              <button
                type="button"
                onClick={() => {
                  onChange(opt);
                  onPicked();
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition ${
                  active
                    ? 'bg-[var(--st-violet)]/15 ring-1 ring-[var(--st-violet)]/40 text-white'
                    : 'text-white/85 hover:bg-white/10'
                }`}
              >
                <span>{String(opt)}</span>
                {active && <Check className="h-3.5 w-3.5 text-[var(--st-violet)]" />}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }
  if (param && param.widget === 'toggle') {
    const on = !!(value ?? param.default);
    return (
      <div className="space-y-2">
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => onChange(!on)}
          className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 transition ${
            on
              ? 'border-[var(--st-violet)]/60 bg-[var(--st-violet)]/15'
              : 'border-white/10 bg-white/[0.04] hover:bg-white/[0.08]'
          }`}
        >
          <span
            className={`text-sm font-semibold ${on ? 'text-white' : 'text-white/70'}`}
          >
            {on ? 'On' : 'Off'}
          </span>
          {/* Track */}
          <span
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              on ? 'bg-[var(--st-violet)]' : 'bg-white/15'
            }`}
          >
            {/* Thumb */}
            <span
              className={`absolute h-5 w-5 rounded-full bg-white shadow transition-transform duration-150 ${
                on ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </span>
        </button>
      </div>
    );
  }
  return (
    <DynamicParamField param={param} value={value} onChange={onChange} />
  );
}

/**
 * Model picker drops out below the Model pill. Same row-style as Settings,
 * with brand icon, name, badge, and optional 2-line description.
 */
type ModelCapability =
  | 'universal'
  | 'text-to-image'
  | 'image-edit'
  | 'enhance'
  | 'text-to-video'
  | 'image-to-video'
  | 'video-to-video'
  | 'other';

function modelCapability(model: ModelDTO, type: GenerationType): ModelCapability {
  const id = `${model.id}/${model.subtype ?? ''}/${model.inputType ?? ''}`.toLowerCase();
  if (type === 'image') {
    if (model.variants?.t2i && model.variants?.edit) return 'universal';
    if (id.includes('upscale') || id.includes('enhance') || id.includes('restore')) return 'enhance';
    if (id.includes('image-to-image') || id.includes('/edit') || id.includes('image-edit')) {
      return 'image-edit';
    }
    if (id.includes('text-to-image')) return 'text-to-image';
    return 'other';
  }
  if (model.variants?.t2v && model.variants?.i2v) return 'universal';
  if (id.includes('video-to-video')) return 'video-to-video';
  if (id.includes('image-to-video') || id.includes('reference-to-video')) return 'image-to-video';
  if (id.includes('text-to-video')) return 'text-to-video';
  return 'other';
}

function ModelPickerPopover({
  anchorRef,
  models,
  type,
  loading,
  selectedModel,
  onSelect,
  onShowInfo,
  hasModel,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  models: ModelDTO[];
  type: GenerationType;
  loading: boolean;
  selectedModel: string;
  onSelect: (id: string) => void;
  onShowInfo: () => void;
  hasModel: boolean;
}) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<'featured' | 'all'>('all');
  const visibleModels = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return models.filter((model) => {
      if (!needle && scope === 'featured' && !model.featured) return false;
      if (!needle) return true;
      const haystack = [model.label, model.id, model.brand?.name, model.subtype, model.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return needle.split(/\s+/).every((token) => haystack.includes(token));
    });
  }, [models, query, scope]);
  const capabilityLabels: Record<ModelCapability, string> = {
    universal: t(
      type === 'image'
        ? 'studio.modelPicker.capability.universalImage'
        : 'studio.modelPicker.capability.universalVideo'
    ),
    'text-to-image': t('studio.modelPicker.capability.textToImage'),
    'image-edit': t('studio.modelPicker.capability.imageEdit'),
    enhance: t('studio.modelPicker.capability.enhance'),
    'text-to-video': t('studio.modelPicker.capability.textToVideo'),
    'image-to-video': t('studio.modelPicker.capability.imageToVideo'),
    'video-to-video': t('studio.modelPicker.capability.videoToVideo'),
    other: t('studio.modelPicker.capability.other'),
  };
  // Keep provider ranking across capabilities: otherwise a newly released
  // text-only model could be buried beneath dozens of older paired models.
  const groupedModels = [{ capability: 'catalog', label: t('studio.modelPicker.all'), models: visibleModels }];
  return (
    <PortalPopover anchorRef={anchorRef} placement="bottom-start">
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex max-h-[inherit] w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[var(--st-line)] bg-[var(--st-panel)]/95 text-white shadow-2xl backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-150 ease-out"
    >
      <div className="shrink-0 border-b border-white/10 p-3">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-white/60">
              {t('studio.generator.model')}
            </h3>
            <p className="mt-0.5 text-[10px] text-white/40">
              WaveSpeed ·{' '}
              {type === 'image'
                ? t('studio.modelPicker.imageModels')
                : t('studio.modelPicker.videoModels')}{' '}
              · {models.length}
            </p>
          </div>
        {hasModel && (
          <button
            type="button"
            onClick={onShowInfo}
            className="rounded-full p-1 text-white/60 transition hover:bg-white/10 hover:text-white"
            title={t('studio.generator.modelInfo')}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        )}
        </div>
        <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-2.5 py-2 focus-within:border-[var(--st-violet)]/60">
          <Search className="h-3.5 w-3.5 shrink-0 text-white/45" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('studio.modelPicker.search')}
            aria-label={t('studio.modelPicker.search')}
            autoFocus
            className="min-w-0 flex-1 bg-transparent text-xs text-white outline-none placeholder:text-white/35"
          />
        </label>
        <div className="mt-2 flex gap-1 rounded-xl bg-white/[0.04] p-1">
          {(['featured', 'all'] as const).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setScope(item)}
              className={`flex-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold transition ${
                scope === item ? 'bg-white/12 text-white' : 'text-white/45 hover:text-white/75'
              }`}
            >
              {item === 'featured'
                ? t('studio.modelPicker.featured')
                : t('studio.modelPicker.all')}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2" style={{ maxHeight: '30rem' }}>
        {loading ? (
          <div className="space-y-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 w-full animate-pulse rounded-xl bg-white/5" />
            ))}
          </div>
        ) : visibleModels.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-white/60">
            {t('studio.select.noModels')}
          </p>
        ) : (
          <div className="space-y-3">
            {groupedModels.map((group) => (
              <section key={group.capability} aria-labelledby={`model-group-${group.capability}`}>
                <div className="flex items-center justify-between px-2 py-1.5">
                  <h4
                    id={`model-group-${group.capability}`}
                    className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/45"
                  >
                    {group.label}
                  </h4>
                  <span className="text-[10px] tabular-nums text-white/30">
                    {group.models.length}
                  </span>
                </div>
                <ul className="space-y-1">
                  {group.models.map((m) => {
                    const isActive = m.id === selectedModel;
                    const capability = capabilityLabels[modelCapability(m, type)];
                    const providerAndCapability = [m.brand?.name, capability]
                      .filter(Boolean)
                      .join(' · ');
                    const badgeColor =
                      m.badge === 'HOT'
                        ? 'bg-orange-500/20 text-orange-200'
                        : m.badge === 'NEW'
                        ? 'bg-emerald-500/20 text-emerald-200'
                        : m.badge === 'PRO'
                        ? 'bg-violet-500/25 text-violet-100'
                        : '';
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          onClick={() => onSelect(m.id)}
                          className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition ${
                            isActive
                              ? 'bg-[var(--st-violet)]/15 ring-1 ring-[var(--st-violet)]/50'
                              : 'bg-white/[0.04] hover:bg-white/[0.08]'
                          }`}
                        >
                          <BrandIcon
                            modelId={m.id}
                            fallbackLetter={m.brand?.letter ?? m.label.charAt(0).toUpperCase()}
                            size={32}
                            rounded="lg"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="truncate text-sm font-semibold text-white">{m.label}</p>
                              {m.badge && (
                                <span
                                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${badgeColor}`}
                                >
                                  {m.badge}
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 truncate text-[11px] text-white/55">
                              {providerAndCapability}
                            </p>
                          </div>
                          <CatalogPrice modelId={m.id} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
    </PortalPopover>
  );
}

/**
 * References popover — pill-rows for each reference kind (images / videos /
 * audios), expanding inline to show the uploader for just that kind.
 */
function ReferencesPopover({
  anchorRef,
  mediaParams,
  paramValues,
  setParamValues,
  singleImageParams,
  expandedKey,
  setExpandedKey,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  mediaParams: ParamDef[];
  paramValues: Record<string, any>;
  setParamValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  singleImageParams: ParamDef[];
  expandedKey: string | null;
  setExpandedKey: (k: string | null) => void;
}) {
  const { t } = useI18n();
  const toggle = (k: string) => setExpandedKey(expandedKey === k ? null : k);

  const rows: Array<{
    key: string;
    label: string;
    count: number;
    max: number;
    body: React.ReactNode;
  }> = [];

  for (const param of mediaParams.filter((p) => p.widget === 'media-list')) {
    const label = t(param.mediaKind === 'video' ? 'studio.generator.referenceVideos'
      : param.mediaKind === 'audio' ? 'studio.generator.referenceAudios' : 'studio.generator.referenceImages');
    const max = param.maxItems ?? 20;
    rows.push({
      key: param.key, label, count: (paramValues[param.key] ?? []).length, max,
      body: <ReferenceImages value={paramValues[param.key] ?? []}
        onChange={(value) => setParamValues((prev) => ({ ...prev, [param.key]: value }))}
        max={max} label={label} hint={param.description} kind={param.mediaKind ?? 'image'} />,
    });
  }

  // Per-row DOM refs so the sub-picker can anchor to the active row.
  const rowRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const subAnchorRef = useMemo<React.RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        return expandedKey ? rowRefs.current[expandedKey] : null;
      },
      set current(_) {
        /* derived */
      },
    }),
    [expandedKey]
  );

  // Find the currently-expanded row's title + content for the sub-picker.
  const expandedRow = rows.find((r) => r.key === expandedKey);
  const expandedSingleImage = singleImageParams.find((p) => p.key === expandedKey);
  const subTitle = expandedRow?.label
    ?? (expandedSingleImage ? expandedSingleImage.key.replace(/_/g, ' ') : '');

  return (
    <>
      <PortalPopover anchorRef={anchorRef} placement="bottom-start">
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex max-h-[inherit] w-[19rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[var(--st-line)] bg-[var(--st-panel)]/95 text-white shadow-2xl backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-150 ease-out"
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            <ul className="space-y-1">
              {singleImageParams.map((p) => (
                <SettingsRow
                  key={p.key}
                  k={p.key}
                  label={p.key.replace(/_/g, ' ')}
                  value={paramValues[p.key] ? '✓' : '—'}
                  active={expandedKey === p.key}
                  onActivate={() => setExpandedKey(p.key)}
                  registerRef={(el) => {
                    rowRefs.current[p.key] = el;
                  }}
                />
              ))}
              {rows.map((r) => (
                <SettingsRow
                  key={r.key}
                  k={r.key}
                  label={r.label}
                  value={`${r.count} / ${r.max}`}
                  active={expandedKey === r.key}
                  onActivate={() => setExpandedKey(r.key)}
                  registerRef={(el) => {
                    rowRefs.current[r.key] = el;
                  }}
                />
              ))}
            </ul>
          </div>
        </div>
      </PortalPopover>

      {expandedKey && (
        <PortalPopover
          key={expandedKey}
          anchorRef={subAnchorRef}
          placement="right-start"
        >
          <SubPicker title={subTitle} onClose={() => setExpandedKey(null)}>
            {expandedSingleImage ? (
              <DynamicParamField
                param={expandedSingleImage}
                value={paramValues[expandedSingleImage.key]}
                onChange={(v) =>
                  setParamValues((prev) => ({
                    ...prev,
                    [expandedSingleImage.key]: v,
                  }))
                }
              />
            ) : (
              expandedRow?.body
            )}
          </SubPicker>
        </PortalPopover>
      )}
    </>
  );
}

/** Fetch discounted base prices only for rows that actually enter the viewport. */
function CatalogPrice({ modelId }: { modelId: string }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const quote = useWavespeedQuote(modelId, undefined, visible);
  return <div ref={ref} className="shrink-0 text-right">
    <p className="text-[11px] font-semibold text-emerald-200">
      {quote.price === undefined ? (quote.error ? '—' : '…') : <WavespeedPrice quote={quote} />}
    </p>
    <p className="text-[9px] text-white/35">{t('studio.modelPicker.from')}</p>
  </div>;
}
